import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Grid,
  IconButton,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Stop,
  Refresh,
  Visibility,
} from '@mui/icons-material';
import { Campaign } from '../../store/api/outboundCallingApi';

interface CampaignListProps {
  campaigns: Campaign[];
  onCampaignAction: (campaignId: number, action: string) => void;
  onViewDetails: (campaignId: number) => void;
  loadingCampaignId?: number | null;
}

const CampaignList: React.FC<CampaignListProps> = ({ campaigns, onCampaignAction, onViewDetails, loadingCampaignId }) => {
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [campaignToStop, setCampaignToStop] = useState<Campaign | null>(null);
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'stopped':
        return 'error';
      case 'completed':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <PlayArrow />;
      case 'paused':
        return <Pause />;
      case 'stopped':
        return <Stop />;
      case 'completed':
        return <Refresh />;
      default:
        return undefined;
    }
  };

  const getProgressPercentage = (campaign: Campaign) => {
    if (campaign.total_contacts === 0) return 0;
    return Math.round((campaign.completed_contacts / campaign.total_contacts) * 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleStopClick = (campaign: Campaign) => {
    setCampaignToStop(campaign);
    setStopDialogOpen(true);
  };

  const handleStopConfirm = () => {
    if (campaignToStop) {
      onCampaignAction(campaignToStop.id, 'stop');
      setStopDialogOpen(false);
      setCampaignToStop(null);
    }
  };

  const handleStopCancel = () => {
    setStopDialogOpen(false);
    setCampaignToStop(null);
  };

  // Sort campaigns by creation date (newest first for display)
  const sortedCampaigns = [...campaigns].sort((a, b) => 
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  // Create a map of campaign ID to chronological number (oldest = 1, newest = highest)
  const chronologicalNumbers = [...campaigns]
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    .reduce((acc, campaign, index) => {
      acc[campaign.id] = index + 1;
      return acc;
    }, {} as Record<number, number>);

  return (
    <>
      <Grid container spacing={3}>
        {sortedCampaigns.map((campaign, index) => (
          <Grid item xs={12} md={6} lg={4} key={campaign.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                        flexShrink: 0
                      }}
                    >
                      {chronologicalNumbers[campaign.id]}
                    </Box>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                      {campaign.name}
                    </Typography>
                  </Box>
                  <Chip
                    icon={getStatusIcon(campaign.status)}
                    label={campaign.status.toUpperCase()}
                    color={getStatusColor(campaign.status) as any}
                    size="small"
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Tag: {campaign.tag_name}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {campaign.total_contacts} contacts • {campaign.interval_minutes}min intervals
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {campaign.timezone} • {campaign.start_hour}:00 - {campaign.end_hour}:00
                </Typography>

                {/* Progress Bar */}
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Progress
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getProgressPercentage(campaign)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getProgressPercentage(campaign)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {campaign.completed_contacts} completed
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {campaign.failed_contacts} failed
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="caption" color="text.secondary">
                  Created: {formatDate(campaign.created_at)}
                </Typography>

                {campaign.started_at && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Started: {formatDate(campaign.started_at)}
                  </Typography>
                )}

                {campaign.completed_at && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Completed: {formatDate(campaign.completed_at)}
                  </Typography>
                )}
              </CardContent>

              <Box sx={{ p: 2, pt: 0 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {campaign.status === 'paused' && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<PlayArrow />}
                      onClick={() => onCampaignAction(campaign.id, 'start')}
                      color="success"
                      disabled={loadingCampaignId === campaign.id}
                    >
                      {loadingCampaignId === campaign.id ? 'Starting...' : 'Start'}
                    </Button>
                  )}

                  {campaign.status === 'active' && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Pause />}
                      onClick={() => onCampaignAction(campaign.id, 'pause')}
                      color="warning"
                    >
                      Pause
                    </Button>
                  )}

                  {campaign.status === 'paused' && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PlayArrow />}
                      onClick={() => onCampaignAction(campaign.id, 'resume')}
                      color="primary"
                    >
                      Resume
                    </Button>
                  )}

                  {(campaign.status === 'active' || campaign.status === 'paused') && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Stop />}
                      onClick={() => handleStopClick(campaign)}
                      color="error"
                    >
                      Stop
                    </Button>
                  )}

                  <Tooltip title="View Details">
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => onViewDetails(campaign.id)}
                    >
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Stop Campaign Confirmation Dialog */}
      <Dialog
        open={stopDialogOpen}
        onClose={handleStopCancel}
        aria-labelledby="stop-dialog-title"
        aria-describedby="stop-dialog-description"
      >
        <DialogTitle id="stop-dialog-title">
          Stop Campaign
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="stop-dialog-description">
            Are you sure you want to stop the campaign "{campaignToStop?.name}"? 
            This action cannot be undone and will permanently stop the campaign.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStopCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleStopConfirm} color="error" variant="contained">
            Stop Campaign
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CampaignList;
